import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("session_id"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (sessionId) {
        try {
          const response = await axios.get(`${API}/me?session_id=${sessionId}`);
          setUser(response.data);
        } catch (err) {
          localStorage.removeItem("session_id");
          setSessionId(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [sessionId]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/login`, { username, password });
      const data = response.data;

      // 2FA required -> return response for LoginPage to handle
      if (data.requires_2fa) {
        return { requires_2fa: true, pending_id: data.pending_id, email_hint: data.email_hint };
      }

      // Direct login
      const { session_id, user: userData } = data;
      localStorage.setItem("session_id", session_id);
      setSessionId(session_id);
      setUser(userData);
      return { success: true };
    } catch (err) {
      return { error: err.response?.data?.detail || "Anmeldung fehlgeschlagen" };
    }
  };

  const complete2FA = async (pendingId, code) => {
    try {
      const response = await axios.post(`${API}/verify-2fa`, { pending_id: pendingId, code });
      const { session_id, user: userData } = response.data;
      localStorage.setItem("session_id", session_id);
      setSessionId(session_id);
      setUser(userData);
      return { success: true };
    } catch (err) {
      return { error: err.response?.data?.detail || "Code ungültig" };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/logout?session_id=${sessionId}`);
    } catch (err) {
      console.error("Logout error:", err);
    }
    localStorage.removeItem("session_id");
    setSessionId(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/me?session_id=${sessionId}`);
      setUser(response.data);
    } catch (err) {}
  };

  return (
    <AuthContext.Provider value={{ user, sessionId, login, complete2FA, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
